/**
 * Import mode selector component
 *
 * Lets users choose how to handle duplicate cards during import:
 * - Import new only (default): Skip cards that match existing collection cards
 * - Update existing: Update quantity on matched cards, add new cards
 * - Overwrite all: Delete all cards from this source, import all
 * - Add all as new: Ignore matching, add everything as new cards
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoAddCircle, IoLayers, IoRefresh, IoSwapHorizontal } from 'react-icons/io5';

import type { MatchingStrategy } from '@/lib/universalCSVParser';

// ============================================================================
// Types
// ============================================================================

export type ImportMode = 'skip' | 'update' | 'overwrite' | 'add_all';

/** Controls how the overwrite option is presented */
export type OverwriteContext =
  { type: 'source'; sourceName: string; count: number } | { type: 'folder'; folderName: string; count: number } | { type: 'disabled' };

export interface ImportModeSelectorProps {
  /** Currently selected mode */
  value: ImportMode;
  /** Called when mode changes */
  onChange: (mode: ImportMode) => void;
  /** Matching strategy from the import (determines which modes are available) */
  matchingStrategy: MatchingStrategy;
  /** Number of duplicate cards detected */
  duplicateCount: number;
  /** Number of new cards detected */
  newCount: number;
  /** Context for the overwrite option */
  overwriteContext: OverwriteContext;
  /** Additional className */
  className?: string;
}

// ============================================================================
// Mode Definitions
// ============================================================================

interface ModeOption {
  value: ImportMode;
  label: string;
  description: string;
  icon: ReactElement;
  /** Whether this mode requires duplicate detection to be available */
  requiresDuplicateDetection: boolean;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    value: 'skip',
    label: 'Import new only',
    description: 'Skip cards that already exist in your collection',
    icon: <IoAddCircle className="h-5 w-5" />,
    requiresDuplicateDetection: true,
  },
  {
    value: 'update',
    label: 'Update existing',
    description: 'Update quantity on matched cards, add new cards',
    icon: <IoRefresh className="h-5 w-5" />,
    requiresDuplicateDetection: true,
  },
  {
    value: 'overwrite',
    label: 'Overwrite all',
    description: 'Delete and re-import',
    icon: <IoSwapHorizontal className="h-5 w-5" />,
    requiresDuplicateDetection: false,
  },
  {
    value: 'add_all',
    label: 'Add all as new',
    description: 'Ignore matching, add all cards as new entries',
    icon: <IoLayers className="h-5 w-5" />,
    requiresDuplicateDetection: false,
  },
];

// ============================================================================
// Component
// ============================================================================

export default function ImportModeSelector({
  value,
  onChange,
  matchingStrategy,
  duplicateCount,
  newCount,
  overwriteContext,
  className,
}: ImportModeSelectorProps): ReactElement {
  const canDetectDuplicates = matchingStrategy !== 'none';

  // Build overwrite description and availability from context
  const overwriteAvailable = overwriteContext.type !== 'disabled' && overwriteContext.count > 0;
  const overwriteDescription =
    overwriteContext.type === 'source'
      ? `Delete all cards from ${overwriteContext.sourceName} and re-import`
      : overwriteContext.type === 'folder'
        ? `Delete all cards in ${overwriteContext.folderName} and re-import`
        : '';

  // Filter options based on what's available
  const availableOptions = MODE_OPTIONS.filter((option) => {
    if (option.requiresDuplicateDetection && !canDetectDuplicates) return false;
    if (option.requiresDuplicateDetection && duplicateCount === 0) return false;
    if (option.value === 'overwrite' && !overwriteAvailable) return false;
    return true;
  });

  // If no duplicates detected, skip/update modes aren't useful - show a note
  const noDuplicatesDetected = canDetectDuplicates && duplicateCount === 0;

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-pearl text-sm font-medium">Import Mode</h4>
        {canDetectDuplicates && (
          <span className="text-ash text-xs">
            {newCount} new • {duplicateCount} duplicate{duplicateCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* No duplicate detection available notice */}
      {!canDetectDuplicates && (
        <div className="bg-gold/10 text-gold rounded-lg p-3 text-sm">
          <p className="font-medium">Duplicate detection unavailable</p>
          <p className="mt-1 text-xs opacity-80">
            This import doesn't have the IDs needed for duplicate detection. All cards will be added as new entries.
          </p>
        </div>
      )}

      {/* Matching strategy info */}
      {canDetectDuplicates && (
        <p className="text-ash text-xs">
          Matching by:{' '}
          <span className="text-silver">
            {matchingStrategy === 'source_id' ? 'Delver Local ID' : 'Card fingerprint (Scryfall ID + finish + condition)'}
          </span>
        </p>
      )}

      {/* No duplicates found notice */}
      {noDuplicatesDetected && (
        <div className="bg-success/10 text-success rounded-lg p-3 text-sm">
          <p>No duplicates found — all {newCount} cards are new to your collection.</p>
        </div>
      )}

      {/* Mode radio buttons */}
      <div className="space-y-2">
        {availableOptions.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={option.value}
              className={clsx(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-all',
                isSelected ? 'border-aether/50 bg-aether/10' : 'border-storm hover:border-storm/80 hover:bg-slate/30',
              )}
            >
              <input
                type="radio"
                name="importMode"
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="border-storm bg-slate focus-ring mt-0.5 h-4 w-4 accent-current"
              />
              <div className={clsx('flex-1', isSelected ? 'text-pearl' : 'text-silver')}>
                <div className="flex items-center gap-2">
                  <span className={clsx(isSelected ? 'text-aether' : 'text-ash')}>{option.icon}</span>
                  <span className="font-medium">{option.label}</span>
                </div>
                <p className={clsx('mt-0.5 text-xs', isSelected ? 'text-silver' : 'text-ash')}>
                  {option.value === 'overwrite' ? overwriteDescription : option.description}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
